import { Badge, Group, Progress, Stack, Text } from '@cowbell/ui';

import type { ProofOfLossItem } from '../../api/claims/responses/ProofOfLossResponses';
import { rem } from '../../components/panels/panelLayout';
import { DocumentDropzone } from '../../console/claims/detail/financials/DocumentDropzone';

interface ProofOfLossItemRowProps {
  readonly item: ProofOfLossItem;
  /** Another row is uploading, or this one is. */
  readonly disabled: boolean;
  /** Percent sent, while this row's file is on its way; null otherwise. */
  readonly progress: number | null;
  readonly error?: string;
  readonly onFile: (file: File) => void;
}

const noop = () => undefined;

export function ProofOfLossItemRow({
  item,
  disabled,
  progress,
  error,
  onFile,
}: ProofOfLossItemRowProps) {
  return (
    <Stack gap="xs" data-testid={`proof-row-${item.key}`}>
      <Group justify="space-between" gap="sm">
        <Text fw={600}>{item.label}</Text>
        <Badge variant="light" color={item.required ? 'blue' : 'gray'}>
          {item.required ? 'Required' : 'Optional'}
        </Badge>
      </Group>
      {item.note && (
        <Text size="sm" c="orange.8">
          {`Your adjuster asked for this again: ${item.note}`}
        </Text>
      )}
      <DocumentDropzone
        label="Choose a file or drop it here"
        ariaLabel={`Upload ${item.label}`}
        disabled={disabled}
        onDrop={(files) => files[0] && onFile(files[0])}
        onRemove={noop}
      />
      {progress !== null && (
        <Progress
          value={progress}
          size={rem(6)}
          aria-label={`Uploading ${item.label}`}
        />
      )}
      {error && (
        <Text size="sm" c="red.8" role="alert">
          {error}
        </Text>
      )}
    </Stack>
  );
}
